/**
 * Admin Ambassador Member Detail Page
 *
 * Shows full details for a single ambassador: profile, score history, NFTs, admin actions.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  getAdminMemberDetail,
  adjustScore,
  suspendMember,
  banMember,
  reinstateMember,
  resetMonthlyCounter,
  deleteMember,
} from '../api/ambassadors';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || '-';
  return `${addr.slice(0, 8)}...${addr.slice(-4)}`;
}

export function AdminAmbassadorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getAdminMemberDetail(id);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load ambassador detail:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleAction = async (action: string) => {
    if (!id) return;
    const reason = prompt(`Reason for ${action}:`);
    if (!reason) return;

    setActionLoading(true);
    try {
      if (action === 'suspend') await suspendMember(id, reason);
      else if (action === 'ban') await banMember(id, reason);
      else if (action === 'reinstate') await reinstateMember(id, reason);
      else if (action === 'reset-monthly') await resetMonthlyCounter(id, reason);
      await fetchData();
    } catch (err: any) {
      alert(err.message || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to permanently delete this ambassador record? This will remove all score history, allocations, and allow the user to re-apply. This cannot be undone.')) return;
    setActionLoading(true);
    try {
      await deleteMember(id);
      navigate('/admin/ambassadors?tab=members');
    } catch (err: any) {
      alert(err.message || 'Failed to delete ambassador');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustScore = async () => {
    if (!id) return;
    const amountStr = prompt('Score adjustment (positive or negative number):');
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount)) { alert('Invalid number'); return; }
    const reason = prompt('Reason for adjustment:');
    if (!reason) return;

    setActionLoading(true);
    try {
      await adjustScore(id, amount, reason);
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to adjust score');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <AdminLayout><div className="py-8 text-center text-gray-500">Loading...</div></AdminLayout>;
  }

  if (!data) {
    return <AdminLayout><div className="py-8 text-center text-red-600">Ambassador not found</div></AdminLayout>;
  }

  const { ambassador, user, scoreLog, allocations } = data;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Back link */}
        <button
          onClick={() => navigate('/admin/ambassadors?tab=members')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Back to Members
        </button>

        {/* Profile */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {user?.username || formatAddr(ambassador.wallet_address)}
              </h2>
              <p className="text-sm text-gray-500">{ambassador.wallet_address}</p>
              {user?.email && <p className="text-sm text-gray-500">{user.email}</p>}
              <div className="mt-2 flex items-center gap-4">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  ambassador.status === 'active' ? 'bg-green-100 text-green-800' :
                  ambassador.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {ambassador.status.charAt(0).toUpperCase() + ambassador.status.slice(1)}
                </span>
                <span className="text-sm text-gray-500">Joined {formatDate(ambassador.joined_at)}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">{Number(ambassador.score).toFixed(1)}</div>
              <div className="text-sm text-gray-500">
                {ambassador.rank ? `Rank #${ambassador.rank}` : 'Unranked'}
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-4 text-center border-t pt-4">
            <div>
              <div className="text-lg font-semibold">{ambassador.total_free_mints}</div>
              <div className="text-xs text-gray-500">Free Mints (Total)</div>
            </div>
            <div>
              <div className="text-lg font-semibold">{ambassador.monthly_mints_count}</div>
              <div className="text-xs text-gray-500">Free Mints (Month)</div>
            </div>
            <div>
              <div className="text-lg font-semibold">{allocations?.total || 0}</div>
              <div className="text-xs text-gray-500">Total Allocations</div>
            </div>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Admin Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleAdjustScore} disabled={actionLoading}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
              Adjust Score
            </button>
            {ambassador.status === 'active' && (
              <>
                <button onClick={() => handleAction('suspend')} disabled={actionLoading}
                  className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-sm text-yellow-800 hover:bg-yellow-100 disabled:opacity-50">
                  Suspend
                </button>
                <button onClick={() => handleAction('ban')} disabled={actionLoading}
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-800 hover:bg-red-100 disabled:opacity-50">
                  Ban
                </button>
              </>
            )}
            {ambassador.status === 'suspended' && (
              <button onClick={() => handleAction('reinstate')} disabled={actionLoading}
                className="rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-sm text-green-800 hover:bg-green-100 disabled:opacity-50">
                Reinstate
              </button>
            )}
            <button onClick={() => handleAction('reset-monthly')} disabled={actionLoading}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
              Reset Monthly Counter
            </button>
            <button onClick={handleDelete} disabled={actionLoading}
              className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-800 hover:bg-red-100 disabled:opacity-50">
              Delete Member
            </button>
          </div>
        </div>

        {/* Score History */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Score History</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Change</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Score After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {scoreLog?.logs?.map((log: any) => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
                        {log.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{log.description}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      {log.score_change_pct != null ? (
                        <span className="text-red-600">-{log.score_change_pct}%</span>
                      ) : log.score_change != null ? (
                        <span className={Number(log.score_change) >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {Number(log.score_change) >= 0 ? '+' : ''}{Number(log.score_change).toFixed(1)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{Number(log.score_after).toFixed(1)}</td>
                  </tr>
                ))}
                {(!scoreLog?.logs || scoreLog.logs.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No score history</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Allocations */}
        {allocations?.allocations?.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Ambassador NFTs</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Launch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Claimed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Lock Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Violation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">X Post</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {allocations.allocations.map((a: any) => (
                    <tr key={a.id}>
                      <td className="px-4 py-3 text-sm">{a.launch_name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          a.status === 'claimed' ? 'bg-green-100 text-green-800' :
                          a.status === 'expired' ? 'bg-gray-100 text-gray-600' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {a.claimed_at ? formatDate(a.claimed_at) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {a.lock_expires_at ? formatDate(a.lock_expires_at) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {a.violation_type ? (
                          <span className="text-red-600 capitalize">{a.violation_type}</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {a.x_post_url ? (
                          <a href={a.x_post_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View
                          </a>
                        ) : a.x_post_missed ? (
                          <span className="text-red-600">Missed</span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
